"use client";

import { ChangeEvent, useState } from "react";
import { BriefPdf, generateUUID, MAX_BRIEF_PDFS, toast } from "@drpcrd/utils";

const CHUNK_SIZE = 5 * 1024 * 1024;

const chunkFile = (file: File) => {
  if (file.size <= CHUNK_SIZE) {
    return [file];
  }
  const chunks: Blob[] = [];
  for (let start = 0; start < file.size; start += CHUNK_SIZE) {
    chunks.push(file.slice(start, Math.min(start + CHUNK_SIZE, file.size)));
  }
  return chunks;
};

/**
 * Uploads one PDF to S3 through the host app's multipart upload routes and
 * resolves with the key it was stored under.
 */
const uploadPdf = async (file: File): Promise<string> => {
  const initRes = await fetch("/api/aws/initiate-multipart-upload", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ fileType: file.type, purpose: "brief-pdf" }),
  });
  if (!initRes.ok) {
    throw new Error("Failed to initiate multipart upload");
  }
  const { UploadId: uploadId, Key: key } = await initRes.json();

  const chunks = chunkFile(file);
  const urlsForm = new FormData();
  urlsForm.append("uploadId", uploadId);
  urlsForm.append("key", key);
  urlsForm.append("parts", chunks.length.toString());
  const urlsRes = await fetch("/api/aws/generate-presigned-urls-parts", {
    method: "POST",
    body: urlsForm,
  });
  if (!urlsRes.ok) {
    throw new Error("Failed to generate presigned upload URLs");
  }
  const urls: string[] = await urlsRes.json();

  const parts = await Promise.all(
    chunks.map(async (chunk, index) => {
      const url = urls[index];
      if (!url) {
        throw new Error(`Missing upload URL for chunk ${index + 1}`);
      }
      const res = await fetch(url, {
        method: "PUT",
        body: chunk,
        headers: { "Access-Control-Expose-Headers": "Location, ETag" },
      });
      if (!res.ok) {
        throw new Error(`Failed to upload chunk ${index + 1}`);
      }
      return { ETag: res.headers.get("ETag") || "", PartNumber: index + 1 };
    }),
  );

  const completeForm = new FormData();
  completeForm.append("uploadId", uploadId);
  completeForm.append("parts", JSON.stringify(parts));
  completeForm.append("key", key);
  const completeRes = await fetch("/api/aws/complete-multi-upload", {
    method: "POST",
    body: completeForm,
  });
  if (!completeRes.ok) {
    throw new Error("Failed to complete multipart upload");
  }
  const { Key } = await completeRes.json();
  return Key;
};

type PendingUpload = { id: string; name: string };

type Options = {
  /** How many PDFs the brief already has, so the cap counts them too. */
  attachedCount: number;
  onUploaded: (pdf: BriefPdf) => void;
};

/**
 * Uploads the PDFs picked in a file input, several at once, and hands each
 * one back as it lands. Enforces the brief's PDF cap across what is already
 * attached and what is still in flight, and reports a failed file with a
 * toast without stopping the others.
 */
export const useBriefPdfUploads = ({ attachedCount, onUploaded }: Options) => {
  const [pending, setPending] = useState<PendingUpload[]>([]);

  const onFilesSelected = async (event: ChangeEvent<HTMLInputElement>) => {
    const picked = Array.from(event.target.files ?? []);
    // Clear the input so picking the same file again still fires a change.
    event.target.value = "";

    const files = picked.filter((file) => file.type === "application/pdf");
    if (files.length < picked.length) {
      toast.error("Only PDF files can be attached");
    }
    if (files.length === 0) {
      return;
    }

    const room = Math.max(MAX_BRIEF_PDFS - attachedCount - pending.length, 0);
    if (files.length > room) {
      toast.error(`You can attach up to ${MAX_BRIEF_PDFS} PDFs`, {
        description:
          room > 0
            ? `Only the first ${room} of your selection will be added`
            : "Remove one to add another",
      });
    }

    const uploads = files
      .slice(0, room)
      .map((file) => ({ id: generateUUID(), name: file.name, file }));
    setPending((previous) => [
      ...previous,
      ...uploads.map(({ id, name }) => ({ id, name })),
    ]);

    await Promise.all(
      uploads.map(async ({ id, name, file }) => {
        try {
          const key = await uploadPdf(file);
          onUploaded({ id: key, name });
        } catch {
          toast.error(`Couldn’t upload ${name}`, {
            description: "Please try again",
          });
        } finally {
          setPending((previous) =>
            previous.filter((upload) => upload.id !== id),
          );
        }
      }),
    );
  };

  return { pending, onFilesSelected };
};
